import React from 'react';
import { IMetaProps, Meta } from './Meta';
import { ITitleProps, Title } from './Title';
import { IOpenGraphProps, OpenGraph } from './OpenGraph';
import { ITwitterCardProps, TwitterCard } from './TwitterCard';
import { useI18next } from 'gatsby-plugin-react-i18next';

import config from '../../../gatsby-config';
import { useLanguageLinkBuilder } from '../../util/i18n';

const siteName = config.siteMetadata?.title as string | undefined ?? 'axesCheck';
const configSiteUrl = config.siteMetadata?.siteUrl as string | undefined;

export interface ISeoProps extends IMetaProps, Omit<ITitleProps, 'children'> {
    title?: string;
    openGraphProps?: IOpenGraphProps;
    twitterCardProps?: ITwitterCardProps;
}

interface ISeoComposition {
    Meta: typeof Meta;
    Title: typeof Title;
    TwitterCard: typeof TwitterCard;
    OpenGraph: typeof OpenGraph;
}

const Seo: React.FunctionComponent<ISeoProps> & ISeoComposition = props => {
    const { t, language } = useI18next('common');
    const buildLanguageLink = useLanguageLinkBuilder(configSiteUrl ?? '');
    const twitterHandle = t('common:footer.socialMedia.twitter.href').split('/').pop();
    const url = buildLanguageLink();

    const {
        title: twitterTitle = props.title,
        siteHandle = twitterHandle,
        ...twitterProps
    } = props.twitterCardProps ?? {};

    const {
        title: ogTitle = props.title,
        ...ogProps
    } = props.openGraphProps ?? {};

    return (
        <>
            {props.title && (<Title titleSuffix={props.titleSuffix}>{props.title}</Title>)}
            <Meta {...props} />
            <TwitterCard
                title={twitterTitle}
                siteHandle={siteHandle}
                url={url}
                description={props.description}
                {...twitterProps}
            />
            <OpenGraph
                title={ogTitle}
                locale={language}
                siteName={siteName}
                url={url}
                description={props.description}
                {...ogProps}
            />
        </>
    );
};

Seo.Meta = Meta;
Seo.Title = Title;
Seo.TwitterCard = TwitterCard;
Seo.OpenGraph = OpenGraph;

export {
    Seo,
};
